import { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

interface IGeographyDataPoint {
  hub: string;
  successful: number;
  failed: number;
}

interface IGeographyStackedBarChartProps {
  data: IGeographyDataPoint[];
  yAxisMax: number;
}

const GeographyStackedBarChart = ({
  data,
  yAxisMax,
}: IGeographyStackedBarChartProps) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) {
      return;
    }

    rootRef.current?.dispose();

    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
      })
    );

    const legend = chart.children.unshift(
      am5.Legend.new(root, {
        x: am5.p100,
        centerX: am5.p100,
        useDefaultMarker: true,
      })
    );

    legend.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(0x63738a),
    });

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 25,
    });

    xRenderer.labels.template.setAll({
      rotation: -45,
      centerX: am5.p100,
      centerY: am5.p100,
      fill: am5.color(0x63738a),
      fontSize: 11,
      paddingTop: 10,
      oversizedBehavior: 'truncate',
      maxWidth: 110,
    });

    xRenderer.grid.template.setAll({
      stroke: am5.color(0xd8dde6),
      strokeOpacity: 1,
      strokeDasharray: [2, 2],
      location: 0,
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'hub',
        renderer: xRenderer,
      })
    );

    xAxis.data.setAll(data);

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({
      fill: am5.color(0x63738a),
      fontSize: 12,
    });

    yRenderer.grid.template.setAll({
      stroke: am5.color(0xd8dde6),
      strokeOpacity: 1,
      strokeDasharray: [2, 2],
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: yAxisMax,
        strictMinMax: true,
        numberFormat: '#,###',
        renderer: yRenderer,
      })
    );

    const successfulSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Successful Print',
        xAxis,
        yAxis,
        valueYField: 'successful',
        categoryXField: 'hub',
        stacked: true,
      })
    );

    successfulSeries.columns.template.setAll({
      fill: am5.color(0x1c6fe6),
      strokeOpacity: 0,
      width: am5.percent(70),
      tooltipText: '{name}: {valueY.formatNumber("#,###")}',
    });

    successfulSeries.data.setAll(data);

    const failedSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Failed Print',
        xAxis,
        yAxis,
        valueYField: 'failed',
        categoryXField: 'hub',
        stacked: true,
      })
    );

    failedSeries.columns.template.setAll({
      fill: am5.color(0x081f5f),
      strokeOpacity: 0,
      width: am5.percent(70),
      tooltipText: '{name}: {valueY.formatNumber("#,###")}',
      cornerRadiusTL: 4,
      cornerRadiusTR: 4,
    });

    failedSeries.data.setAll(data);
    legend.data.setAll([successfulSeries, failedSeries]);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [data, yAxisMax]);

  return (
    <div
      className="amcharts-wrapper"
      data-testid="bar-chart"
      ref={chartRef}
      style={{ width: '100%', height: '450px' }}
    />
  );
};

export default GeographyStackedBarChart;
